import React, { ChangeEvent, useState } from "react";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../ui-kit/k-modal/k-modal-footer";
import KuikaButton, {
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaModalIconWrapper from "../../ui-kit/kuika-modal-icon-wrapper";
import KuikaH5 from "../../ui-kit/typography/H5/H5";
import KuikaText from "../../ui-kit/typography/Text/Text";
import "./branching-modal.scss";

export interface IChangesFoundModalProps {
  showModal: boolean;
  commitMessage: string;
  inProgress?: boolean;
  onClose: () => void;
  onCommitMessageChange: (value: string) => void;
  onIgnoreChangesAndMerge: () => void;
  onCommitAndMerge: () => void;
}

/** The two ways out of the dialog; each runs its own step before the merge. */
type ChangesFoundAction = "commit" | "ignore";

// Busy words only: the merge that follows is carried by its own toast, so neither button shows a ✓.
const COMMIT_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Committing" };
const IGNORE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Discarding" };

const ChangesFoundModal: React.FC<IChangesFoundModalProps> = ({
  showModal,
  commitMessage,
  inProgress,
  onClose,
  onCommitMessageChange,
  onIgnoreChangesAndMerge,
  onCommitAndMerge
}) => {
  // Which button started the step in flight, so that one — not always COMMIT — says it is working.
  const [runningAction, setRunningAction] = useState<ChangesFoundAction>("commit");

  const statusOf = (action: ChangesFoundAction): KuikaButtonStatus =>
    inProgress && runningAction === action ? "loading" : "idle";

  const handleIgnore = (): void => {
    setRunningAction("ignore");
    onIgnoreChangesAndMerge();
  };

  const handleCommit = (): void => {
    setRunningAction("commit");
    onCommitAndMerge();
  };

  const footer = (
    <KModalFooter
      submitLabel="Commit & merge"
      submitTestId="changesFoundCommitAndMergeButton"
      onSubmit={handleCommit}
      status={statusOf("commit")}
      statusLabels={COMMIT_STATUS_LABELS}
      disabled={(inProgress && runningAction !== "commit") || !commitMessage.trim()}
    >
      <KuikaButton
        type="secondary"
        color="neutral"
        size="md"
        uppercase
        onClick={handleIgnore}
        status={statusOf("ignore")}
        statusLabels={IGNORE_STATUS_LABELS}
        disabled={inProgress && runningAction !== "ignore"}
        data-testid="changesFoundIgnoreAndMergeButton"
      >
        Ignore changes & merge
      </KuikaButton>
    </KModalFooter>
  );

  return (
    <KModal showModal={showModal} closeModal={onClose} header="Changes Found" footer={footer} width={460} centered>
      <div className="kuika-branching-modal">
        <div className="kuika-branching-modal__header">
          <KuikaModalIconWrapper iconName="git-commit" intent="warning" />
          <div className="kuika-branching-modal__title">
            <KuikaH5>Changes Found</KuikaH5>
          </div>
        </div>
        <div className="kuika-branching-modal__content">
          <div className="kuika-branching-modal__description">
            <KuikaText>
              There are changes waiting to be commited to the branch. Would you like to continue to merge?
            </KuikaText>
          </div>
          <div className="kuika-branching-modal__form">
            <div className="kuika-branching-modal__form-item">
              <KuikaInput
                type="textarea"
                data-testid="changesFoundCommitMessageInput"
                placeholder="Commit message (required to commit & merge)"
                value={commitMessage}
                onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  onCommitMessageChange(e.target.value)
                }
                disabled={inProgress}
                autoFocus
              />
            </div>
          </div>
        </div>
      </div>
    </KModal>
  );
};

export default ChangesFoundModal;
